import React, { useReducer, useEffect, useState } from 'react';
import {
    Grid,
    Box,
    Dialog,
    DialogTitle,
    DialogContent,
    TextField,
    DialogActions,
    Button,
    Alert,
    AlertTitle,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    CircularProgress,
} from '@mui/material';
import SelectMulti from '../../../../../components/select/SelectMulti';
import MonthPickerComponent from '../../../../../components/date/MonthPickerComponent';
import {toast} from 'react-toastify';
import axios from "axios";


const initialState = {
    month: '',
    publisher: '',
    final_cost: '',
    file_link: '',
    file: null,
    comment: '',
    cost: '',
    id: '',
};

function getInitialState(editRow) {
    let periodString = '';
    if (editRow.period) {
        const parts = editRow.period.split('-');
        if (parts.length >= 2) {
            periodString = `${parts[0]}-${parseInt(parts[1], 10)}`;
        }
    }
    return {
        ...initialState,
        month: periodString,
        publisher: editRow.publisherOriginal || '',
        final_cost: editRow.final_cost || '',
        file_link: editRow.file || '',
        comment: editRow.revision_comment || '',
        cost: editRow.cost || '',
        id: editRow.id || '',
    };
}

function reducer(state, action) {
    switch (action.type) {
        case 'set':
            return { ...state, [action.field]: action.value };
        case 'reset':
            return initialState;
        default:
            return state;
    }
}


function EditRowDialog({
                           open,
                           handleClose,
                           ssp,
                           editRow,
                           onSubmitSuccess,
                       }) {
    const [state, dispatch] = useReducer(reducer, getInitialState(editRow));
    const [loading, setLoading] = useState(true);

    useEffect(() => {
            setLoading(false);
    }, [editRow]);

    useEffect(() => {
        dispatch({ type: 'set', field: 'publisher', value: editRow.publisherOriginal });
        dispatch({ type: 'set', field: 'final_cost', value: editRow.final_cost });
        dispatch({ type: 'set', field: 'file_link', value: editRow.file });
        dispatch({ type: 'set', field: 'comment', value: editRow.revision_comment });
        dispatch({ type: 'set', field: 'cost', value: editRow.cost });
        dispatch({ type: 'set', field: 'id', value: editRow.id });
        let periodString = '';
        if (editRow.period) {
            const parts = editRow.period.split('-');
            if (parts.length >= 2) {
                periodString = `${parts[0]}-${parseInt(parts[1], 10)}`;
            }
        }
        dispatch({ type: 'set', field: 'month', value: periodString });
    }, [editRow]);


    const [endpoint, setEndpoint] = useState('/api/financialtools/xml/totalpayout/edit');
    const BASE_URL = process.env.REACT_APP_BASE_URL;
    const handleMonthChange = (selectedMonth) => {
        const periodString = `${selectedMonth.year}-${selectedMonth.month}`;
        dispatch({ type: 'set', field: 'month', value: periodString });
    };
    const handleInputChange = (event) => {
        dispatch({ type: 'set', field: event.target.name, value: event.target.value });
    };

    const handleInputFileChange = (event) => {
        dispatch({ type: 'set', field: event.target.name, value: event.target.files[0] });
    };

    const validateFormData = () => {
        const requiredFields = [state.publisher, state.final_cost, state.cost];
        return requiredFields.every(field => Boolean(field));
    };

    const sendToServer = async () => {
        if (!validateFormData()) {
            toast.error('Fill required fields first!');
            return;
        }

        try {
            const userId = localStorage.getItem('user_id');
            const formData = new FormData();
            const formFields = {
                file: state.file,
                user_id: userId,
                publisher: state.publisher,
                final_cost: state.final_cost,
                file_link: state.file_link,
                month: state.month,
                cost: state.cost,
                comment: state.comment,
                id: state.id
            };

            Object.keys(formFields).forEach(key => {
                if (formFields[key]) formData.append(key, formFields[key]);
            });

            const response = await axios.post(`${BASE_URL}${endpoint}`, formData, {
                headers: { 'Content-Type': 'multipart/form-data' },
            });

            if (response.data.success) {
                toast.success(response.data.message);
                onSubmitSuccess();
                dispatch({ type: 'reset' });
                handleClose();
            } else {
                toast.error('Error: ' + response.data.message);
            }
        } catch (error) {
            toast.error(`An error occurred while sending data: ${error.message}`);
            console.error('An error occurred while sending data:', error);
        }
    };

    return (
        <Dialog open={open} onClose={handleClose}>
            <DialogTitle>Edit {editRow.publisherDisplay}</DialogTitle>
            <DialogContent>
                {loading ? (
                    <Grid container justifyContent="center">
                        <Grid item>
                            <Box display="flex" justifyContent="center" alignItems="center" minHeight={200}>
                                <CircularProgress size={150}/>
                            </Box>
                        </Grid>
                    </Grid>
                ):(<>
                <Alert variant="outlined" severity="info" color="info" sx={{marginBottom: "20px"}}>
                    Please provide the new values in this form for updating.<br/>
                </Alert>

                <div
                    style={{
                        userSelect: 'none',
                        borderRadius: '5px',
                        textAlign: "center",
                        backgroundColor: '#f0f3f5',
                        padding: '2px',
                        cursor: 'pointer',
                        border: '1px solid #dddfe1',
                    }}
                >
                    <div>
                        <InputLabel id="publisher-label">Choose a period</InputLabel>
                    </div>
                    <div>
                        <MonthPickerComponent onMonthChange={handleMonthChange} initialMonthYear={editRow.period}/>
                    </div>
                </div>

                <FormControl fullWidth margin="dense">
                    <InputLabel id="publisher-label">Publisher</InputLabel>
                    <Select
                        labelId="publisher-label"
                        id="publisher"
                        name="publisher"
                        value={state.publisher ?? editRow.publisherOriginal}
                        onChange={handleInputChange}
                        required={true}
                        label="Publisher"
                    >
                        {ssp.map((ssp) => (
                            <MenuItem key={ssp.value.id} value={ssp.value.id}>
                                {ssp.value.name} (ID: {ssp.value.id})
                            </MenuItem>
                        ))}
                    </Select>
                </FormControl>

                <TextField
                    margin="dense"
                    id="cost"
                    label="Cost"
                    type="number"
                    fullWidth
                    name="cost"
                    value={state.cost}
                    onChange={handleInputChange}
                    required={true}
                />

                <TextField
                    margin="dense"
                    id="final_cost"
                    label="Final Cost"
                    type="number"
                    fullWidth
                    name="final_cost"
                    value={state.final_cost}
                    onChange={handleInputChange}
                    required={true}
                />

                <TextField
                    margin="dense"
                    id="file_link"
                    label="File Link"
                    type="text"
                    fullWidth
                    name="file_link"
                    value={state.file_link}
                    onChange={handleInputChange}
                />

                <div>
                    <InputLabel id="publisher-label">Or choose a file</InputLabel>
                </div>
                <div>
                    <input
                        type="file"
                        id="file"
                        name="file"
                        onChange={handleInputFileChange}
                    />
                </div>

                <TextField
                    margin="dense"
                    id="comment"
                    label="Revision Comment"
                    type="text"
                    fullWidth
                    name="comment"
                    value={state.comment}
                    onChange={handleInputChange}
                    multiline
                    rows={3}
                /></>)}
            </DialogContent>
            <DialogActions>
                <Button onClick={handleClose} color="primary">
                    Cancel
                </Button>
                {!loading && (
                    <Button onClick={sendToServer} color="primary">
                        Save
                    </Button>
                )}
            </DialogActions>
        </Dialog>
    );
}

export default EditRowDialog;